import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Container, Card, CardHeader, CardBody, CardFooter, Row, Col, Button, Spinner } from "reactstrap";
import CommonForm from "Components/Common/CommonForm";
import { usePermission } from "context/PermissionContext";
import { GetUserData, showToastMessage } from "common/service/CommonService";
import { useMasterStudyService } from "modules/Clinical/StudyDetails/api/useStudyQuery";
import { ACCESSIONING_PATHS } from "Routes/paths";
import { useScanningSessionService } from "../api/useScanningSession";
import {
  useScanSampleCollectionSessionSelectAllService,
  useScanSampleSeperationSessionSelectAllService,
} from "modules/Session/Session/api/useSession";
import { getNewAccessioningFields } from "../helper/scanningconstant";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import favicon from "../../../assets/images/favicon.png";
import landingStyles from "../css/clinicalActivityLanding.module.scss";

interface NewScanningSessionModalProps {
  isOpen?: boolean;
  toggle?: () => void;
  redirectPathPattern?: string;
  isNewSessionFlow: boolean;
  onViewSession?: (session: any) => void;
  defaultCategory?: "sample_collection" | "sample_separation" | "dosing";
  handleLogout?: () => void;
}

type AccessioningFormValues = {
  studycode: string;
  period: string;
  timepoint: string;
  dosingStation?: string;
};

const NewScanningSessionModal: React.FC<NewScanningSessionModalProps> = ({
  isOpen = true,
  toggle,
  redirectPathPattern,
  isNewSessionFlow,
  onViewSession,
  defaultCategory = "sample_collection",
  handleLogout,
}) => {
  const [sessionCategory, setSessionCategory] = React.useState<"sample_collection" | "sample_separation" | "dosing">(defaultCategory);

  useEffect(() => {
    setSessionCategory(defaultCategory);
  }, [defaultCategory, isOpen]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AccessioningFormValues>({
    defaultValues: {
      studycode: "",
      period: "",
      timepoint: "",
      dosingStation: "",
    },
  });

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  const handleBack = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    try {
      reset();
    } catch (err) {
      console.warn("Reset error:", err);
    }
    if (toggle) {
      toggle();
    }
  };

  const selectedStudyCode = watch("studycode");
  const selectedPeriod = watch("period");
  const selectedTimepoint = watch("timepoint");

  useEffect(() => {
    setValue("period", "");
    setValue("timepoint", "");
    setValue("dosingStation", "");
  }, [selectedStudyCode, setValue]);

  useEffect(() => {
    setValue("timepoint", "");
    setValue("dosingStation", "");
  }, [selectedPeriod, setValue]);

  useEffect(() => {
    setValue("dosingStation", "");
  }, [selectedTimepoint, setValue]);

  const { DropDownAllQuery: studySelectAllQuery } = useMasterStudyService({ enabledDropDown: true });
  const { hasPermission } = usePermission();
  const hasAllStudiesRight = hasPermission(PERMISSION_MODULES.GLOBAL_STUDIES, PERMISSION_ACTIONS.ALL_STUDIES);
  const user = GetUserData();
  const userName = user?.username || "Investigator";

  const studyOptions: Array<{
    label: string | number;
    value: string | number;
  }> = hasAllStudiesRight
      ? (((studySelectAllQuery.data as any)?.data || []).map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })))
      : ((user?.studies as any[])?.map((study: any) => ({
        label: study.studycode,
        value: study.studyid,
      })) || []);

  const { SelectQuery: studyDetailQuery } = useMasterStudyService({
    id: selectedStudyCode ? Number(selectedStudyCode) : null,
    enabledSelect: true,
  });

  const studyDetail = (studyDetailQuery.data as any)?.data?.[0];

  const isSeparation = sessionCategory === "sample_separation";

  // Call ScanSampleCollection/Session/SelectAll API when coming from Sample Collection and study is selected
  const { SelectAllQuery: collectionSessionQuery } =
    useScanSampleCollectionSessionSelectAllService({
      studyid: selectedStudyCode ? Number(selectedStudyCode) : null,
      enabled: sessionCategory === "sample_collection" && !!selectedStudyCode,
    });

  // Call ScanSampleSeperation/Session/SelectAll API when coming from Sample Separation and study is selected
  const { SelectAllQuery: seperationSessionQuery } =
    useScanSampleSeperationSessionSelectAllService({
      studyid: selectedStudyCode ? Number(selectedStudyCode) : null,
      enabled: sessionCategory === "sample_separation" && !!selectedStudyCode,
    });

  const activeSessionsRaw = React.useMemo(() => {
    if (sessionCategory === "sample_separation") {
      return seperationSessionQuery?.data?.data || [];
    }
    return collectionSessionQuery?.data?.data || [];
  }, [sessionCategory, seperationSessionQuery?.data?.data, collectionSessionQuery?.data?.data]);

  const completedSessions = React.useMemo(() => {
    if (!Array.isArray(activeSessionsRaw)) return [];
    return activeSessionsRaw.filter((s: any) => {
      if (!s || s.summarycard) return false;
      const state = String(s.sessionstate || "").toLowerCase();
      const status = String(s.display_status || "").toLowerCase();
      return state === "completed" || status === "completed";
    });
  }, [activeSessionsRaw]);

  const isDosingStationCompleted = React.useCallback(
    (periodId: string | number, timepointId: string | number, stationKey: string) => {
      if (sessionCategory === "sample_separation") return false;

      const numMatch = stationKey.match(/\d+/);
      const stationNum = numMatch ? parseInt(numMatch[0], 10) : Number(stationKey);

      return completedSessions.some((s: any) => {
        const periodMatch = String(s.periodid) === String(periodId);
        const timepointMatch = String(s.timepointid) === String(timepointId);
        const stationMatch = Number(s.dosingstationno) === stationNum || String(s.dosingstationno).toLowerCase() === stationKey.toLowerCase();
        return periodMatch && timepointMatch && stationMatch;
      });
    },
    [sessionCategory, completedSessions]
  );

  const isTimepointCompleted = React.useCallback(
    (periodObj: any, timepointObj: any) => {
      if (!periodObj || !timepointObj) return false;

      if (sessionCategory === "sample_collection") {
        const subjectsPerStation = periodObj.subjectsperstation;
        const stationKeys = subjectsPerStation && typeof subjectsPerStation === "object" ? Object.keys(subjectsPerStation) : [];

        if (stationKeys.length > 0) {
          return stationKeys.every((key) => isDosingStationCompleted(periodObj.period_id, timepointObj.timepoint_id, key));
        } else {
          return completedSessions.some((s: any) => {
            const periodMatch = String(s.periodid) === String(periodObj.period_id);
            const timepointMatch = String(s.timepointid) === String(timepointObj.timepoint_id);
            return periodMatch && timepointMatch;
          });
        }
      } else {
        return completedSessions.some((s: any) => {
          const periodMatch = String(s.periodid) === String(periodObj.period_id);
          const timepointMatch = String(s.timepointid) === String(timepointObj.timepoint_id);
          return periodMatch && timepointMatch;
        });
      }
    },
    [sessionCategory, completedSessions, isDosingStationCompleted]
  );

  const isPeriodCompleted = React.useCallback(
    (periodObj: any) => {
      const allTimepoints = studyDetail?.timepoints || [];
      if (allTimepoints.length === 0) return false;
      return allTimepoints.every((tp: any) => isTimepointCompleted(periodObj, tp));
    },
    [studyDetail?.timepoints, isTimepointCompleted]
  );

  const periodOptions =
    studyDetail?.periods
      ?.filter((p: any) => !isPeriodCompleted(p))
      .map((p: any) => ({
        label: `${p.period_number}`,
        value: p.period_id,
      })) || [];

  const selectedPeriodObj = studyDetail?.periods?.find(
    (p: any) => String(p.period_id) === String(selectedPeriod)
  );

  const timepointOptions =
    studyDetail?.timepoints
      ?.filter((tp: any) => {
        if (!selectedPeriodObj) return true;
        return !isTimepointCompleted(selectedPeriodObj, tp);
      })
      .map((tp: any) => ({
        label: tp.timepoint_number,
        value: tp.timepoint_id,
      })) || [];

  const subjectsPerStation = selectedPeriodObj?.subjectsperstation;

  const dosingStationOptions =
    subjectsPerStation && typeof subjectsPerStation === "object"
      ? Object.keys(subjectsPerStation)
        .filter((key: string) => {
          if (!selectedPeriod || !selectedTimepoint) return true;
          return !isDosingStationCompleted(selectedPeriod, selectedTimepoint, key);
        })
        .sort((a, b) => {
          const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
          const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
          return numA - numB;
        })
        .map((key: string) => {
          const numMatch = key.match(/\d+/);
          const stationNum = numMatch ? numMatch[0].padStart(2, "0") : key;
          return {
            label: `Dosing Station ${stationNum}`,
            value: key,
          };
        })
      : [];

  const protocolFields = getNewAccessioningFields("protocol", {
    studyOptions,
  });

  const isDosing = sessionCategory === "dosing";

  const periodFields = getNewAccessioningFields("period", {
    selectedStudyCode,
    selectedPeriod,
    periodOptions,
    timepointOptions,
    sessionCategory,
    dosingStationOptions: [
      { label: dosingStationOptions.length > 0 ? "Select Dosing Station" : "No Dosing Stations Available", value: "" },
      ...dosingStationOptions
    ]
  });

  const navigate = useNavigate();
  const { InsertMutation, ValidateMutation } = useScanningSessionService();

  const onSubmit = (data: AccessioningFormValues) => {
    console.log("data :", data)
    const isSeparation = sessionCategory === "sample_separation";
    const dsVal = isSeparation ? "" : (data.dosingStation || "");
    const stationNum = isSeparation ? null : (dsVal ? (parseInt(String(dsVal).replace(/\D/g, ""), 10) || dsVal) : 0);

    const payload = {
      ...data,
      timepoint: isDosing ? "Dosing" : data.timepoint,
      sessiontype: sessionCategory,
      dosingstationno: stationNum,
      dosingStation: dsVal || undefined,
    };

    const getTargetRoute = (targetPath: string) => {
      if (sessionCategory === "dosing") return `${targetPath}/dosing`;
      if (sessionCategory === "sample_separation") return `${targetPath}/step2`;
      return `${targetPath}/step1`;
    };

    if (isNewSessionFlow) {
      if (data.studycode) {
        InsertMutation.mutate(payload, {
          onSuccess: (res: any) => {
            showToastMessage(
              "success",
              res?.message || `${isDosing ? "IP Dosing" : sessionCategory === "sample_separation" ? "Sample Separation" : "Sample Collection"} session initialized successfully`,
            );
            const targetPath = (redirectPathPattern || ACCESSIONING_PATHS.SESSION).replace(
              ":id",
              res.data.sessionid,
            );
            if (toggle) toggle();
            navigate(getTargetRoute(targetPath));
          },
          onError: (error: any) => {
            const errMsg =
              typeof error === "string" && error.trim().length > 0
                ? error
                : error?.response?.data?.message ||
                  error?.data?.message ||
                  error?.message ||
                  "Failed to initialize session";

            showToastMessage("error", errMsg);
          },
        });
      }
    } else {
      // Validate session via AccessioningSession/Validate API
      const validatePayload = {
        studyid: Number(data.studycode),
        periodid: Number(data.period),
        timepointid: data.timepoint,
        dosingstationno: isSeparation ? null : Number(stationNum),
      };

      ValidateMutation.mutate(validatePayload, {
        onSuccess: (res: any) => {
          const resData = res?.data;
          const sessionId = resData?.sessionid;

          if (sessionId) {
            showToastMessage(
              "success",
              res?.message || "Accessioning Session record validated successfully!"
            );
            const selectedStudyObj = studyOptions.find((s: any) => String(s.value) === String(data.studycode));
            const sessionDataObj = {
              ...resData,
              sessionid: sessionId,
              studyid: Number(data.studycode),
              studycode: selectedStudyObj?.label || data.studycode,
              sessiontype: sessionCategory,
              dosingstationno: isSeparation ? null : Number(stationNum),
              dosingStation: dsVal,
            };

            if (onViewSession) {
              onViewSession(sessionDataObj);
            } else {
              const targetPath = (redirectPathPattern || ACCESSIONING_PATHS.SESSION).replace(
                ":id",
                String(sessionId)
              );
              if (toggle) toggle();
              navigate(getTargetRoute(targetPath));
            }
          } else {
            showToastMessage("error", res?.message || "Session validation failed: Session ID not returned.");
          }
        },
        onError: (error: any) => {
          const errMsg =
            typeof error === "string" && error.trim().length > 0
              ? error
              : error?.response?.data?.message ||
                error?.data?.message ||
                error?.message ||
                "Failed to validate session";
          showToastMessage("error", errMsg);
        },
      });

      /* 
      // Commented out previous client-side session/selectall matching code:
      const selectedPeriodObj = studyDetail?.periods?.find(
        (p: any) => String(p.period_id) === String(data.period)
      );
      const selectedPeriodNumber = selectedPeriodObj ? selectedPeriodObj.period_number : null;

      const sessions = SelectAllQuery?.data?.data || [];
      const matchingSession = sessions.find((s: any) => {
        if (s.summarycard) return false;

        const isStudyMatch = Number(s.studyid) === Number(data.studycode);
        const isPeriodMatch =
          Number(s.period_number) === Number(selectedPeriodNumber) ||
          Number(s.periodid) === Number(data.period);

        const isTimepointMatch = isDosing ? true : String(s.timepoint_number) === String(data.timepoint);

        const isStationMatch =
          !s.dosingstationno ||
          Number(s.dosingstationno) === Number(stationNum);

        return isStudyMatch && isPeriodMatch && isTimepointMatch && isStationMatch;
      });

      if (matchingSession) {
        if (onViewSession) {
          onViewSession(matchingSession);
        }
      } else {
        showToastMessage("error", "No active session found for the selected parameters.");
      }
      */
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ width: "100%", minHeight: "100vh", paddingTop: "50px", display: "flex", flexDirection: "column", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}>
      {/* ── Top Bar Header ── */}
      <div className={landingStyles.topHeader} style={{ position: "fixed", top: 0, left: 0, right: 0, height: "56px", zIndex: 1000, backgroundColor: "#ffffff" }}>
        <div className={landingStyles.brandGroup}>
          <img src={favicon} alt="Vista Logo" />
          <h5 className={landingStyles.brandTitle}>Accessioning Session</h5>
        </div>

        <div className={landingStyles.userBadge}>
          <div className={landingStyles.userInfo}>
            <i className="ri-user-3-line"></i>
            <span>{userName}</span>
          </div>

          {handleLogout && (
            <button
              type="button"
              className={landingStyles.logoutBtn}
              onClick={handleLogout}
              title="Logout"
            >
              <i className="ri-logout-box-r-line"></i>
            </button>
          )}
        </div>
      </div>

      {/* ── Main Viewport Content ── */}
      <div className="flex-grow-1 px-2 d-flex align-items-center justify-content-center" style={{ overflowY: "auto", paddingTop: "20px"}}>
        <Container style={{ maxWidth: "560px" }}>
          <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
            {/* Header with Back Arrow & Title */}
            <CardHeader className="bg-white border-bottom px-4 py-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                {toggle && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light border text-dark p-0 d-inline-flex align-items-center justify-content-center shadow-none"
                    onClick={handleBack}
                    title="Back"
                    style={{ width: "32px", height: "32px", borderRadius: "6px" }}
                  >
                    <i className="ri-arrow-left-line fs-18"></i>
                  </button>
                )}
                <h5 className="mb-0 fw-bold text-dark fs-16">
                  {isNewSessionFlow
                    ? isDosing
                      ? "New IP Dosing Session"
                      : sessionCategory === "sample_separation"
                      ? "New Sample Separation Session"
                      : "New Accessioning Session"
                    : isDosing
                      ? "Select Existing Dosing Session"
                      : sessionCategory === "sample_separation"
                      ? "Select Existing Sample Separation Session"
                      : "Select Existing Session"}
                </h5>
              </div>
            </CardHeader>

            {/* Form Body */}
            <CardBody className="px-4 py-3 bg-white">
              <div className="mb-2">
                <h6 className="fw-bold text-dark mb-1 fs-14">
                  {isDosing
                    ? "Dosing Session Parameters"
                    : sessionCategory === "sample_separation"
                    ? "Sample Separation Parameters"
                    : "Sample Collection Parameters"}
                </h6>
                <p className="text-muted mb-0 fs-12">
                  {isDosing
                    ? "Select study code, period, and dosing station for drug administration."
                    : sessionCategory === "sample_separation"
                    ? "Select the study details for the samples you are about to separate into aliquots."
                    : "Select the study details for the samples you are about to accession."}
                </p>
              </div>

              <div>
                <CommonForm
                  fields={protocolFields}
                  control={control}
                  errors={errors}
                  columns={1}
                />
              </div>

              <div>
                <CommonForm
                  fields={periodFields}
                  control={control}
                  errors={errors}
                  columns={1}
                />
              </div>
            </CardBody>

            {/* Form Footer */}
            <CardFooter className="bg-white border-top px-4 py-2.5 d-flex align-items-center justify-content-end gap-2">
              {toggle && (
                <Button color="secondary" outline onClick={handleBack} className="px-3">
                  Cancel
                </Button>
              )}
              <Button
                color={isDosing ? "success" : "primary"}
                onClick={handleSubmit(onSubmit)}
                disabled={isNewSessionFlow ? InsertMutation.isPending : ValidateMutation.isPending}
                className="px-3 d-flex align-items-center gap-1"
              >
                {(isNewSessionFlow && InsertMutation.isPending) || (!isNewSessionFlow && ValidateMutation.isPending) ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-play-line me-1"></i>
                )}
                {isNewSessionFlow ? "Initialize Session" : "View Session"}
              </Button>
            </CardFooter>
          </Card>
        </Container>
      </div>
    </div>
  );
};

export default NewScanningSessionModal;

